/** @param {string} text */
function p(text) {
  return { type: "paragraph", html: `<p>${text}</p>` };
}

/** @param {string} text */
function quote(text) {
  return { type: "quote", text };
}

/** @param {string} imageKey @param {string} caption */
function fig(imageKey, caption) {
  return {
    type: "figure",
    imageKey,
    caption: `[ ${caption} ]`,
  };
}

export const AI_EMPOWERED_INDIVIDUAL_META = {
  heroCaption: "[ fishermen hauling the net ]",
  eyebrow: "TECHNOLOGY · FIELD NOTES",
  tags: ["ai", "product", "technology", "generalist", "trust"],
  authorBio:
    "Product leader, photographer, storyteller. Builds things by day, thinks out loud here the rest of the time.",
};

export const AI_EMPOWERED_INDIVIDUAL_BODY = [
  {
    type: "lead",
    text: "For most of my career in product development, my role was never to be the best designer, engineer, or business analyst in the room. My role was to understand enough about each discipline to bring people together and turn an idea into reality.",
  },
  p(
    "Product people sit in the middle. We work with business stakeholders to understand problems, designers to shape experiences, and engineers to understand technical constraints. We connect the dots."
  ),
  p(
    "Ten years ago, that meant being heavily dependent on specialists. If I had an idea, I would need a designer to create mockups. If they were busy, I waited. If I needed to test a technical concept, I needed engineering time. If the team was overloaded, I waited. If I needed research, I spent hours reading documentation, searching forums, and piecing information together from dozens of sources. The pace of execution was often limited by the availability of the people around me."
  ),
  p("AI changes that."),
  p(
    "Today, if I have an idea for a new product, I can generate concepts and visual designs within a few hours. If I need to explore a technical solution, I can use tools such as Cursor, Claude, ChatGPT, or Codex to help me build prototypes and test ideas. If I need to understand an unfamiliar technology, I can get answers in minutes rather than spending days searching for them."
  ),
  p(
    "The specialists still matter. Great designers are still better designers than me. Great engineers are still better engineers than me. Great lawyers are still better lawyers than me. But AI dramatically reduces the distance between having an idea and being able to explore it."
  ),
  p(
    "This is why I believe AI will disproportionately benefit people who know a little about a lot of things. The generalists. The people who understand business, technology, design, operations, marketing, and strategy at a reasonable level. The people who can move comfortably between different domains and understand how they connect."
  ),
  p("AI gives these people leverage."),
  p(
    "Not because AI replaces expertise, but because it helps fill knowledge gaps quickly enough to maintain momentum. A product person who understands the fundamentals of software development can now build things that previously required waiting weeks for engineering capacity. Someone with a reasonable understanding of design can rapidly explore concepts without waiting for a design sprint. Someone with business knowledge can analyse markets, competitors, and opportunities far faster than before."
  ),
  p("There is, however, a misconception about AI that I see frequently."),
  quote(
    "Many people seem to think AI is Jarvis from Iron Man. They imagine a future where they can simply say, \"Build me a billion dollar company,\" or \"Create me a revolutionary application,\" and AI will do the rest."
  ),
  p("That is not how AI works."),
  p("At least not today."),
  p(
    "The real power of AI comes from detail. The more context, constraints, goals, requirements, edge cases, and future considerations you provide, the better the outcome becomes. Building a successful application still requires thinking about user behaviour, scalability, security, business models, adoption, maintenance, and long term evolution. AI can help you think through those challenges. It cannot think through them for you."
  ),
  p(
    "In many ways, AI rewards people who are willing to go deeper. The better your understanding of a problem, the better your ability to guide the AI towards a useful outcome. The quality of the output is still heavily dependent on the quality of the thinking behind it."
  ),
  p(
    "This is why I think product people are uniquely positioned to benefit. We are trained to ask questions. We challenge assumptions. We think about trade offs. We connect business goals with technical realities. We consider not only how something should work today, but how it might evolve over time. AI makes those capabilities significantly more powerful."
  ),
  fig("cjmeister-com-_CJ06596.jpg", "connecting the dots on the wall"),
  p("But there is another side to this story."),
  p(
    "The same tools that empower capable people also empower bad actors. Creating fake content has never been easier. Creating convincing misinformation has never been easier. Creating fake expertise has never been easier. AI lowers the barrier to creation for everyone, including people with good intentions and people without them."
  ),
  p("As a result, I think we are heading towards an information crisis."),
  p(
    "Even today, I often find myself reading an article and wondering whether it is real. Did a person write this? Is this based on genuine experience? Is the author actually knowledgeable about the subject, or is this simply AI generated content designed to sound convincing?"
  ),
  p(
    "For most of human history, creating information was expensive. Writing took effort. Research took effort. Publishing took effort. AI is rapidly driving those costs towards zero. The challenge of the next decade may not be producing information. It may be verifying it."
  ),
  p("Trust may become one of the most valuable commodities in the digital world."),
  p(
    "Ironically, as AI makes it easier to create content, human credibility may become more important than ever. The ability to demonstrate genuine expertise, genuine experience, and genuine authenticity could become a significant competitive advantage."
  ),
  p("So while many people focus on whether AI will replace jobs, I think something more interesting is happening."),
  p("AI is becoming a force multiplier."),
  p(
    "The people who combine curiosity, broad knowledge, critical thinking, and the ability to connect ideas across disciplines are going to become incredibly powerful. Not because AI is doing the work for them, but because AI allows them to move faster than ever before."
  ),
  p(
    "The future may not belong to specialists or generalists alone. It may belong to the people who know enough to ask the right questions, think critically about the answers, and use AI as a tool rather than a crutch."
  ),
  p(
    "Of course, it is difficult to capture everything I think about AI in a single blog post. There are plenty of exceptions to the points I've made here, and there are entire topics that I have deliberately avoided because this article would quickly become far too long."
  ),
  p(
    "For example, I have not touched on the impact AI will have on different professions and industries. While AI is likely to have a significant impact on knowledge based work, I suspect there will continue to be enormous value in practical trades that require human hands, physical presence, judgement, and craftsmanship. Plumbers, electricians, carpenters, mechanics, and many other skilled trades may see AI become a useful tool, but the work itself still requires a person to turn up and do it."
  ),
  fig("cjmeister-com-_DSC3671.jpg", "turning an idea into something real"),
  p(
    "I also haven't explored the social, economic, educational, and political implications of AI, all of which deserve discussions of their own. The purpose of this post was not to predict the future of AI, but simply to share one observation: that AI appears to be giving unprecedented leverage to people who can think across disciplines, connect ideas, and understand enough about multiple subjects to guide the technology effectively."
  ),
  p(
    "Whether that ultimately proves to be a good thing or a bad thing is a question that I suspect we are only just beginning to answer."
  ),
];
